"use client";

import React from "react";
import { toast } from "sonner";
import { ModalContainer, MODAL_IDS } from "@wtc/ui";
import { UnifiedModal } from "../UnifiedModal";
import { GoogleDriveBrowser } from "@/components/crm/drive/GoogleDriveBrowser";
import { useCompanyHooks } from "@/lib/hooks/api/shared/companies-graphql";
import {
  CUSTOMER_DRIVE_ROOT_FOLDER_ID,
  companyDriveFolderUrl,
} from "@/lib/integrations/google-drive/customer-root";

// This modal is mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE
// any DetailActionProvider. It therefore reads everything from the global-modal-
// stack data payload (companyId) — never DetailActionContext. The save goes
// through the codegen-typed company update hook. See GLOBAL_MODAL_NO_ACTION_CONTEXT.

export interface ChangeCompanyDriveFolderModalData {
  /** Company whose google_drive_folder_id we are picking. */
  companyId: string;
  /** Optional human label for the toast. */
  companyName?: string;
  /** Currently-linked folder id, for context. */
  currentFolderId?: string;
}

export function ChangeCompanyDriveFolderModal() {
  return (
    <ModalContainer<ChangeCompanyDriveFolderModalData>
      modalId={MODAL_IDS.companies.changeDriveFolder}
    >
      {({ isOpen, data, onClose }) => (
        <ChangeCompanyDriveFolderModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface ContentProps {
  isOpen: boolean;
  data?: ChangeCompanyDriveFolderModalData;
  onClose: () => void;
}

function ChangeCompanyDriveFolderModalContent({ isOpen, data, onClose }: ContentProps) {
  const companyHooks = useCompanyHooks();
  const update = companyHooks.useUpdate();
  const [isSaving, setIsSaving] = React.useState(false);

  const companyId = data?.companyId;

  const handlePick = React.useCallback(
    async (folderId: string, folderName: string) => {
      if (!companyId) {
        toast.error("Unable to set Drive folder — no company in context");
        return;
      }
      setIsSaving(true);
      try {
        await update.mutateAsync({ id: companyId, google_drive_folder_id: folderId });
        toast.success(`Drive folder set to “${folderName}”`, {
          action: {
            label: "Open",
            onClick: () => window.open(companyDriveFolderUrl(folderId), "_blank"),
          },
        });
        onClose();
      } catch (error) {
        console.error("Failed to set company Drive folder:", error);
        toast.error("Failed to set Drive folder");
      } finally {
        setIsSaving(false);
      }
    },
    [companyId, update, onClose],
  );

  return (
    <UnifiedModal
      modalId={MODAL_IDS.companies.changeDriveFolder}
      title="Change Drive folder"
      description="Browse to the Google Drive folder this company should use, then choose “Select this folder”."
      size="2xl"
      maxHeight="85vh"
    >
      <div className="p-4">
        {isSaving && (
          <p className="mb-2 text-sm text-muted-foreground">Saving…</p>
        )}
        {isOpen && companyId ? (
          <GoogleDriveBrowser
            showHeader
            maxHeight="55vh"
            initialParentId={CUSTOMER_DRIVE_ROOT_FOLDER_ID}
            initialParentName="Customer Folders"
            allowDownload={false}
            allowView={false}
            allowDelete={false}
            allowUpload={false}
            allowCreateFolder
            onPickCurrentFolder={handlePick}
          />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No company selected.
          </p>
        )}
      </div>
    </UnifiedModal>
  );
}
